"use client";

import BlogSocialMediaPostingSection from '@/components/admin/BlogSocialMediaPostingSection';
import {
  extraImagesForCompose,
  socialPostingCaptionForCompose,
  socialPostingEnabledForCompose,
  socialPostingTagsForCompose,
} from '@/lib/social-media/image-fetch-urls';
import {
  datetimeLocalToIsoOrNull,
  toDatetimeLocalValue,
} from '@/lib/social-media/facebook-schedule-fields';
import * as Dialog from '@radix-ui/react-dialog';
import { Globe, Loader2, MoreHorizontal, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

type BlogSummary = {
  _id: string;
  title: string;
  status: 'Draft' | 'Published' | 'Scheduled' | 'Preview';
  blogType?: string;
};

type LoadedBlog = {
  featuredImage?: string;
  socialMediaPostingEnabled?: boolean;
  socialMediaPostingUseFeaturedImage?: boolean;
  socialMediaPostingScheduledAt?: string | Date | null;
  socialMediaPostingPublishedAt?: string | Date | null;
  socialMediaPostingCaption?: string;
  socialMediaPostingTags?: string[];
  socialMediaPostingExtraImageUrls?: string[];
  facebookBlogAutoPostId?: string;
  facebookBlogAutoPostError?: string | null;
  socialMediaLinkedPostId?: string;
};

type ToastFn = (message: string, type: 'success' | 'error' | 'info') => void;

const PAGE_NAME = process.env.NEXT_PUBLIC_FACEBOOK_PAGE_NAME || 'Facebook Page';
const PAGE_ID = process.env.NEXT_PUBLIC_FACEBOOK_PAGE_ID?.trim() || '';

function pageAvatarUrl(): string | null {
  if (!PAGE_ID) return null;
  return `https://graph.facebook.com/${encodeURIComponent(PAGE_ID)}/picture?type=small`;
}

function truncateTitle(title: string, max = 48): string {
  const t = title.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

type FacebookContentKind = 'blog' | 'thought_leadership' | 'news' | 'press_release';

function apiBaseForKind(kind: FacebookContentKind): string {
  if (kind === 'thought_leadership') return '/api/admin/thought-leadership';
  if (kind === 'news' || kind === 'press_release') return '/api/admin/news-pr';
  return '/api/admin/blogs';
}

export default function BlogFacebookPostingModal({
  blog,
  open,
  onOpenChange,
  showToast,
  onPosted,
  contentKind = 'blog',
}: {
  blog: BlogSummary | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  showToast: ToastFn;
  onPosted?: (info: { linkedPostId?: string | null; postId?: string; blogId: string }) => void;
  contentKind?: FacebookContentKind;
}) {
  const apiBase = apiBaseForKind(contentKind);
  const itemLabel =
    contentKind === 'thought_leadership'
      ? 'thought leadership'
      : contentKind === 'press_release'
        ? 'press release'
        : contentKind === 'news'
          ? 'news item'
          : 'blog';
  const isSchedulerMode = true;
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingBlog, setLoadingBlog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const [featuredImage, setFeaturedImage] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [publishedAt, setPublishedAt] = useState('');
  const [useFeaturedImage, setUseFeaturedImage] = useState(true);
  const [caption, setCaption] = useState('');
  const [tagsStr, setTagsStr] = useState('');
  const [extraImageUrls, setExtraImageUrls] = useState<string[]>([]);
  const [facebookBlogAutoPostId, setFacebookBlogAutoPostId] = useState('');
  const [facebookBlogAutoPostError, setFacebookBlogAutoPostError] = useState<string | null>(null);
  const [socialMediaLinkedPostId, setSocialMediaLinkedPostId] = useState('');

  const resetFromApi = useCallback((b: LoadedBlog) => {
    setFeaturedImage((b.featuredImage || '').trim());
    setEnabled(socialPostingEnabledForCompose(b));
    setScheduledAt(toDatetimeLocalValue(b.socialMediaPostingScheduledAt));
    setPublishedAt(
      b.socialMediaPostingPublishedAt
        ? new Date(b.socialMediaPostingPublishedAt).toISOString()
        : ''
    );
    setUseFeaturedImage(b.socialMediaPostingUseFeaturedImage !== false);
    setCaption(socialPostingCaptionForCompose(b));
    setTagsStr(socialPostingTagsForCompose(b).join(', '));
    setExtraImageUrls(extraImagesForCompose(b));
    setFacebookBlogAutoPostId((b.facebookBlogAutoPostId || '').trim());
    setFacebookBlogAutoPostError(
      typeof b.facebookBlogAutoPostError === 'string' ? b.facebookBlogAutoPostError : null
    );
    setSocialMediaLinkedPostId((b.socialMediaLinkedPostId || '').trim());
  }, []);

  const clearComposeState = useCallback(() => {
    setFeaturedImage('');
    setEnabled(false);
    setScheduledAt('');
    setPublishedAt('');
    setUseFeaturedImage(true);
    setCaption('');
    setTagsStr('');
    setExtraImageUrls([]);
    setFacebookBlogAutoPostId('');
    setFacebookBlogAutoPostError(null);
    setSocialMediaLinkedPostId('');
    setLoadError(null);
  }, []);

  /** Reset compose inputs only (caption, tags, extra images, checkboxes). */
  const clearComposeFields = useCallback(() => {
    setCaption('');
    setTagsStr('');
    setExtraImageUrls([]);
    setFacebookBlogAutoPostError(null);
    setEnabled(false);
    setScheduledAt('');
    setUseFeaturedImage(true);
  }, []);

  useEffect(() => {
    if (!open) {
       
      clearComposeState();
      return;
    }
    if (!blog?._id) return;

    clearComposeState();

    let cancelled = false;
    (async () => {
      setLoadingBlog(true);
      try {
        const id = encodeURIComponent(String(blog._id).trim());
        const r = await fetch(`${apiBase}/${id}`, { cache: 'no-store' });
        const d = (await r.json()) as {
          blog?: LoadedBlog;
          thoughtLeadership?: LoadedBlog;
          newsPr?: LoadedBlog;
          error?: string;
        };
        if (!r.ok) throw new Error(d.error || `Failed to load ${itemLabel}`);
        const loaded = d.blog ?? d.thoughtLeadership ?? d.newsPr;
        if (!loaded) throw new Error(`${itemLabel} not found`);
        if (cancelled) return;
        resetFromApi(loaded);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load blog');
      } finally {
        if (!cancelled) setLoadingBlog(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, blog?._id, resetFromApi, apiBase, itemLabel, clearComposeState]);

  const buildBody = useCallback(() => {
    const scheduledIso = isSchedulerMode ? datetimeLocalToIsoOrNull(scheduledAt) : null;
    const tags = tagsStr
      .split(/[,\n]/)
      .map((t) => t.trim())
      .filter(Boolean);
    return {
      socialMediaPostingEnabled: isSchedulerMode ? !!scheduledIso : enabled,
      ...(isSchedulerMode ? { socialMediaPostingScheduledAt: scheduledIso } : {}),
      socialMediaPostingUseFeaturedImage: useFeaturedImage,
      socialMediaPostingCaption: caption,
      socialMediaPostingTags: tags,
      socialMediaPostingExtraImageUrls: extraImageUrls,
    };
  }, [caption, enabled, extraImageUrls, isSchedulerMode, scheduledAt, tagsStr, useFeaturedImage]);

  const handleSaveSettings = async () => {
    if (!blog?._id) return;
    setSaving(true);
    try {
      const id = encodeURIComponent(String(blog._id).trim());
      const r = await fetch(`${apiBase}/${id}/social-posting`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody()),
        cache: 'no-store',
      });
      const d = (await r.json()) as { success?: boolean; error?: string };
      if (!r.ok || !d.success) throw new Error(d.error || 'Save failed');
      if (isSchedulerMode) {
        setEnabled(!!datetimeLocalToIsoOrNull(scheduledAt));
      }
      onPosted?.({ blogId: blog._id, linkedPostId: null });
      showToast(isSchedulerMode ? 'Facebook schedule saved.' : 'Facebook settings saved.', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePostToFacebook = async () => {
    if (!blog?._id || isSchedulerMode) return;
    if (blog.status !== 'Published') {
      showToast(
        contentKind === 'thought_leadership'
          ? 'Publish the article first, then post to Facebook.'
          : contentKind === 'press_release'
            ? 'Publish the press release first, then post to Facebook.'
            : contentKind === 'news'
              ? 'Publish the news item first, then post to Facebook.'
          : 'Publish the blog first, then post to Facebook.',
        'info'
      );
      return;
    }
    setPosting(true);
    try {
      const id = encodeURIComponent(String(blog._id).trim());
      const r = await fetch(`${apiBase}/${id}/facebook-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody()),
        cache: 'no-store',
      });
      const d = (await r.json()) as {
        success?: boolean;
        error?: string;
        postId?: string;
        socialMediaLinkedPostId?: string | null;
      };
      if (!r.ok || !d.success) {
        const errMsg = d.error || 'Facebook post failed';
        setFacebookBlogAutoPostError(errMsg);
        throw new Error(errMsg);
      }
      showToast('Posted to Facebook.', 'success');
      if (d.postId) setFacebookBlogAutoPostId(d.postId);
      setFacebookBlogAutoPostError(null);
      onPosted?.({
        linkedPostId: d.socialMediaLinkedPostId ?? null,
        blogId: blog._id,
        ...(d.postId ? { postId: d.postId } : {}),
      });
      onOpenChange(false);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Facebook post failed', 'error');
    } finally {
      setPosting(false);
    }
  };

  const handleDiscardFailedPost = async () => {
    if (!blog?._id) return;
    setDiscarding(true);
    try {
      const id = encodeURIComponent(String(blog._id).trim());
      const r = await fetch(`${apiBase}/${id}/facebook-discard`, {
        method: 'POST',
        cache: 'no-store',
      });
      const d = (await r.json()) as { success?: boolean; error?: string };
      if (!r.ok || !d.success) throw new Error(d.error || 'Discard failed');
      setFacebookBlogAutoPostId('');
      setFacebookBlogAutoPostError(null);
      setSocialMediaLinkedPostId('');
      setEnabled(false);
      showToast('Failed Facebook post cleared. You can try again.', 'success');
      onPosted?.({ blogId: blog._id, linkedPostId: null });
      onOpenChange(false);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Discard failed', 'error');
    } finally {
      setDiscarding(false);
    }
  };

  const busy = saving || posting || discarding;
  const isPublished = blog?.status === 'Published';
  const hasLinkedFb = !!(facebookBlogAutoPostId || socialMediaLinkedPostId);
  const scheduleLocked = isSchedulerMode && hasLinkedFb;
  const showDiscard = !!facebookBlogAutoPostError?.trim() && !hasLinkedFb;
  const avatar = pageAvatarUrl();

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[60] data-[state=open]:animate-in fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[60] w-[min(96vw,500px)] max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-lg bg-[#f0f2f5] p-3 shadow-xl focus:outline-none">
          <div className="sr-only">
            <Dialog.Title>
              Create Facebook post for {itemLabel}
            </Dialog.Title>
            <Dialog.Description>Configure and publish to your Facebook page</Dialog.Description>
          </div>

          <div className="bg-white rounded-lg border border-[#dddfe2] shadow-sm overflow-hidden text-[15px] leading-snug text-[#050505]">
            {/* Header — matches SocialMediaViewModal */}
            <div className="flex items-start gap-2 px-3 pt-3 pb-2 border-b border-[#dddfe2]">
              <div className="h-10 w-10 shrink-0 rounded-full overflow-hidden bg-[#e4e6eb] border border-[#dddfe2]">
                {avatar ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-xs font-bold text-gray-500">
                    {PAGE_NAME.slice(0, 1)}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[15px] leading-5">{PAGE_NAME}</div>
                <div className="flex items-center gap-1 text-[13px] text-[#65676b] mt-0.5 flex-wrap">
                  <span>Create post</span>
                  <span aria-hidden>·</span>
                  <span className="truncate max-w-[min(100%,220px)]" title={blog?.title}>
                    {blog?.title
                      ? truncateTitle(blog.title, 52)
                      : contentKind === 'thought_leadership'
                        ? 'Thought leadership'
                        : contentKind === 'press_release'
                          ? 'Press release'
                          : contentKind === 'news'
                            ? 'News'
                        : 'Blog'}
                  </span>
                  <span aria-hidden>·</span>
                  <Globe className="h-3.5 w-3.5 shrink-0" aria-label="Public" />
                </div>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <Dialog.Close
                  className="rounded-full p-1.5 text-[#65676b] hover:bg-[#f0f2f5]"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </Dialog.Close>
                <button
                  type="button"
                  className="rounded-full p-1.5 text-[#65676b] hover:bg-[#f0f2f5] opacity-40 cursor-default"
                  aria-hidden
                  tabIndex={-1}
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="px-3 pt-3 pb-1">
              {loadError ? (
                <p className="text-sm text-red-600 py-4 text-center">{loadError}</p>
              ) : loadingBlog ? (
                <div className="flex items-center gap-2 py-10 text-[#65676b] justify-center">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="text-sm">Loading…</span>
                </div>
              ) : (
                <>
                  {/*
                    <p className="text-[13px] text-[#65676b] bg-[#f0f2f5] rounded-md px-2.5 py-2 mb-3 border border-[#e7e8eb]">
                      Draft — save settings here; publish the blog, then use <strong className="text-[#050505]">Post</strong>.
                    </p>
                  */}
                  {hasLinkedFb ? (
                    <p className="text-[13px] text-[#1c6e1c] bg-[#e7f3e7] rounded-md px-2.5 py-2 mb-3 border border-[#cce8cc]">
                      A post is already linked. Close this window and use the share icon to view or remove it.
                    </p>
                  ) : null}

                  <BlogSocialMediaPostingSection
                    variant="fbCard"
                    mode={isSchedulerMode ? 'scheduler' : 'manual'}
                    featuredImage={featuredImage}
                    enabled={enabled}
                    scheduledAt={scheduledAt}
                    publishedAt={publishedAt}
                    useFeaturedImage={useFeaturedImage}
                    caption={caption}
                    tagsStr={tagsStr}
                    extraImageUrls={extraImageUrls}
                    showToast={showToast}
                    onScheduledAtChange={(value) => {
                      setScheduledAt(value);
                      setEnabled(!!value);
                    }}
                    onUseFeaturedChange={setUseFeaturedImage}
                    onCaptionChange={setCaption}
                    onTagsStrChange={setTagsStr}
                    onExtraUrlsChange={setExtraImageUrls}
                    {...(facebookBlogAutoPostId ? { facebookBlogAutoPostId } : {})}
                    {...(facebookBlogAutoPostError ? { facebookBlogAutoPostError } : {})}
                    labels={{
                      title: isSchedulerMode ? 'Facebook Post Scheduler' : 'Compose',
                      ...(contentKind === 'thought_leadership'
                        ? {
                            subtitle:
                              'Schedule a Facebook photo post for this article. Caption format: your text, then "Read our article" with the link, then tags as hashtags.',
                            captionPlaceholder:
                              'Optional intro. "Read our article - {link}" and hashtags are added automatically.',
                          }
                        : contentKind === 'press_release'
                          ? {
                              subtitle:
                                'Schedule a Facebook photo post for this press release. Caption format: your text, then "Read our press release" with the link, then tags as hashtags.',
                              captionPlaceholder:
                                'Optional intro. "Read our press release - {link}" and hashtags are added automatically.',
                            }
                          : contentKind === 'news'
                            ? {
                                subtitle:
                                  'Schedule a Facebook photo post for this news item. Caption format: your text, then "Read our news" with the link, then tags as hashtags.',
                                captionPlaceholder:
                                  'Optional intro. "Read our news - {link}" and hashtags are added automatically.',
                              }
                        : {}),
                      ...(isSchedulerMode ? {} : { enabledLabel: 'Enable posting for this item' }),
                    }}
                  />
                </>
              )}
            </div>

            {!loadError && !loadingBlog ? (
              <>
                <div className="h-px bg-[#ced0d4] mx-3" />
                <div className="px-3 py-2 flex flex-wrap items-center gap-2 justify-end bg-white border-t border-[#e7e8eb]">
                  <div className="flex flex-wrap gap-2 mr-auto">
                    {showDiscard ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleDiscardFailedPost()}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#dc2626] bg-white px-3 py-1.5 text-sm font-semibold text-[#dc2626] hover:bg-[#fef2f2] disabled:opacity-50 disabled:pointer-events-none"
                      >
                        {discarding ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Discard failed post
                      </button>
                    ) : null}
                    {!hasLinkedFb ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={clearComposeFields}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#ccd0d5] bg-white px-3 py-1.5 text-sm font-semibold text-[#65676b] hover:bg-[#f0f2f5] disabled:opacity-50 disabled:pointer-events-none"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    disabled={busy || scheduleLocked}
                    onClick={() => void handleSaveSettings()}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#ccd0d5] bg-[#f0f2f5] px-3 py-1.5 text-sm font-semibold text-[#050505] hover:bg-[#e4e6eb] disabled:opacity-50 disabled:pointer-events-none"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {isSchedulerMode ? 'Save schedule' : 'Save settings'}
                  </button>
                  {!isSchedulerMode ? (
                    <button
                      type="button"
                      disabled={busy || !isPublished || hasLinkedFb}
                      onClick={() => void handlePostToFacebook()}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#0866FF] bg-[#0866FF] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#0756d9] disabled:opacity-50 disabled:pointer-events-none"
                    >
                      {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Post to Facebook
                    </button>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
